import { useMemo } from 'react';

import dayjs, { type Dayjs } from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import type { UserTier } from '@/entities/permissions';
import { useAppSelector } from '@/hooks/redux-hooks';
import { useImpersonatorEmailFromStorage } from '@/services/storage';
import { SalesChannel, UserAccess } from '@/types/auth';

dayjs.extend(localizedFormat);

type UserDetailsResult = {
  status: string | undefined;
  userEmail: string | null;
  hsObjectId: string | null;
  isUserBasic: boolean;
  isUserPremium: boolean;
  isUserExpired: boolean | undefined;
  isTrial: boolean | undefined;
  currentSubscription: string;
  formatedExpirationDate: string | undefined;
  salesChannel: SalesChannel;
  expiredDate: Dayjs | undefined;
  terms: string | null;
  impersonatorEmail: string | null | undefined;
  userTier: UserTier;
};

const useUserDetails = (): UserDetailsResult => {
  const { userEmail, hsObjectId, subscription, terms } = useAppSelector(state => state.tripToken);
  const isUserExpired = useAppSelector(state => state.user.isExpired);
  const userTier = useAppSelector(state => state.user.userTier);
  const impersonatorEmail = useImpersonatorEmailFromStorage();

  const isUserPremium = useMemo(
    () => (subscription ? subscription.plan === UserAccess.Premium : true),
    [subscription]
  );

  const isUserBasic = useMemo(
    () => (subscription ? subscription.plan !== UserAccess.Premium : true),
    [subscription]
  );

  const expiredDate = useMemo(() => {
    const expireAt = subscription?.expireAt;
    if (!expireAt) {
      return undefined;
    }
    return dayjs(expireAt.includes('Z') ? expireAt : `${expireAt}Z`);
  }, [subscription]);

  const isTrial = useMemo(() => {
    return subscription?.trial;
  }, [subscription]);

  const salesChannel = useMemo(() => {
    return subscription?.salesChannel ?? SalesChannel.Sales;
  }, [subscription]);

  const currentSubscription = useMemo(() => {
    if (isUserExpired) {
      return 'n/a';
    }
    let base = subscription?.plan ?? 'n/a';

    if (subscription?.trial) {
      base = `${base} Trial`;
    }
    return base;
  }, [isUserExpired, subscription]);

  const formatedExpirationDate = useMemo(() => {
    if (expiredDate) {
      return expiredDate.format('LLLL');
    }
    return undefined;
  }, [expiredDate]);

  return {
    status: subscription?.status,
    userEmail,
    hsObjectId,
    isUserBasic,
    isUserPremium,
    isUserExpired,
    isTrial,
    currentSubscription,
    formatedExpirationDate,
    salesChannel,
    expiredDate,
    terms,
    impersonatorEmail,
    userTier
  };
};

export default useUserDetails;
